import { Input, Layout, Menu, Modal, Switch } from "antd";
import React, { useState } from "react";
import {HomeOutlined, UserOutlined, ShopOutlined, CompassOutlined, CalendarOutlined, PictureOutlined, SettingOutlined } from "@ant-design/icons";
import "./Sidebar.scss";
import config from "../../config.json";
import {useNavigate} from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { setCurrentRoute, setDebugMode, toggleSidebar } from '../../slices/sidebarSlice';
import { useAuth } from '../Login/AuthProvider';
import { Roles } from '../../constants/roles';
import { markLeaveConfirmed } from '../../Helper/navigationConfirm';

const { Sider } = Layout;
const ADMIN_CODE = '2003';

const SideMenu = () => {

  const collapsed = useSelector((state) => state.sidebar.isCollapsed);
  const dispatch = useDispatch();
  const currentRoute = useSelector((state) => state.sidebar.currentRoute);
  const hasUnsavedEditorChanges = useSelector((state) => state.sidebar.hasUnsavedEditorChanges);
  const debugMode = useSelector((state) => state.sidebar.debugMode);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Admin settings — hardcoded PIN gate for internal/debug tooling only.
  const [adminModalOpen, setAdminModalOpen] = useState(false);
  const [adminUnlocked, setAdminUnlocked] = useState(false);
  const [adminCodeInput, setAdminCodeInput] = useState('');
  const [adminCodeError, setAdminCodeError] = useState(false);

  const openAdminModal = () => {
    setAdminModalOpen(true);
    setAdminUnlocked(false);
    setAdminCodeInput('');
    setAdminCodeError(false);
  };
  // Auto-checks as soon as all 4 digits are entered — no separate confirm button.
  const handleAdminCodeChange = (value) => {
    setAdminCodeInput(value);
    if (value.length < ADMIN_CODE.length) {
      setAdminCodeError(false);
      return;
    }
    if (value === ADMIN_CODE) {
      setAdminUnlocked(true);
      setAdminCodeError(false);
    } else {
      setAdminCodeError(true);
    }
  };

  const items = [
    { icon: HomeOutlined, label: 'Home', labelGer: "Home", roles: [Roles.SYSTEM_ADMIN, Roles.CUSTOMER_ADMIN, Roles.USER], children: [] },
    { icon: CalendarOutlined, label: 'Events', labelGer: "Events", roles: [Roles.SYSTEM_ADMIN, Roles.CUSTOMER_ADMIN, Roles.USER], children: [] },
    { icon: CompassOutlined, label: 'Locations', labelGer: "Locations", roles: [Roles.SYSTEM_ADMIN, Roles.CUSTOMER_ADMIN, Roles.USER], children: [] },
    { icon: PictureOutlined, label: 'Images', labelGer: "Bilder", roles: [Roles.SYSTEM_ADMIN, Roles.CUSTOMER_ADMIN, Roles.USER], children: [] },
    { icon: UserOutlined, label: 'Users', labelGer: "Benutzer", roles: [Roles.SYSTEM_ADMIN, Roles.CUSTOMER_ADMIN], children: [] },
    { icon: ShopOutlined, label: 'Customers', labelGer: "Kunden", roles: [Roles.SYSTEM_ADMIN], children: [] },
  ]
    .filter(item => item.roles.includes(user?.role))
    .map((item, index) => {
      const key = String(index + 1);
      return {
        key: item.label.toLowerCase(),
        icon: React.createElement(item.icon),
        label: item.labelGer
        // children: new Array(4).fill(null).map((_, j) => {
        //     const subKey = index * 4 + j + 1;
        //     return {
        //         key: subKey,
        //         label: `Item ${subKey}`,
        //     };
        // }),
      };
    });

  const handleClick = item => {
    if (hasUnsavedEditorChanges) {
      const confirmLeave = window.confirm("Editor wird verlassen, vorgenommene Änderungen werden unter Umständen nicht gespeichert.");
      if (!confirmLeave) {
        return;
      }
      // Already confirmed above — tell the editor's own useBlocker not to ask again.
      markLeaveConfirmed();
    }
    dispatch(setCurrentRoute(item.key));
    navigate(item.key !== 'home' ? item.key : '');
  };

  return (
    <>
    <Sider
      style={{
          position: "fixed",
          height: "calc(100% - 85px)",
          zIndex: "100"
        }}
        className="bg-secondary"
        collapsedWidth={60}
        width={200}
        collapsible
        collapsed={collapsed}
        onCollapse={() => dispatch(toggleSidebar())}
      type="primary"
    >
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Menu
          theme={config.sidebar.theme === 'dark' ? 'dark' : 'light'}
          defaultSelectedKeys={['1']}
          mode="inline"
          items={items}
          className="bg-secondary"
          onClick={handleClick}
          selectedKeys={[currentRoute]}
        />
        {/* Pushed to the bottom of the remaining space, just above the collapse/expand trigger */}
        <div
          className="sidebar-admin-trigger"
          onClick={openAdminModal}
          style={{
            marginTop: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            gap: 10,
            height: 40,
            padding: collapsed ? 0 : '0 24px',
            cursor: 'pointer',
          }}
        >
          <SettingOutlined style={{ fontSize: 16 }} />
          {!collapsed && <span>Einstellungen</span>}
        </div>
      </div>
    </Sider>

    <Modal
      title="Admin-Einstellungen"
      open={adminModalOpen}
      onCancel={() => setAdminModalOpen(false)}
      footer={null}
      destroyOnClose
      width={260}
    >
      {adminUnlocked ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0' }}>
          <span>Debug-Modus aktivieren</span>
          <Switch checked={debugMode} onChange={(checked) => dispatch(setDebugMode(checked))} />
        </div>
      ) : (
        <div style={{ padding: '12px 0', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Input.OTP
            length={ADMIN_CODE.length}
            value={adminCodeInput}
            onChange={handleAdminCodeChange}
            status={adminCodeError ? 'error' : ''}
            mask
            type="password"
            autoFocus
          />
          {adminCodeError && (
            <div style={{ color: 'red', marginTop: 8 }}>Falscher Code.</div>
          )}
        </div>
      )}
    </Modal>
    </>
  );
};

export default SideMenu;
